import { useEffect, useState } from "react";
import { Loader2, Save, Landmark } from "lucide-react";
import { toast } from "sonner";
import { GlassCard } from "../../components/common/ui";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import api from "../../lib/api";

const inp = "mt-1 bg-black/30 border-white/15 text-white";
const EMPTY = { bank_name: "", account_name: "", sort_code: "", account_number: "", enabled: false };

export default function AdminBankDetails() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get("/admin/bank-details").then(({ data }) => setForm({ ...EMPTY, ...data }))
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load bank details"))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/bank-details", form);
      setForm({ ...EMPTY, ...data });
      toast.success("Bank details saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save bank details"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-24 text-white/60"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  return (
    <div className="space-y-6 max-w-2xl" data-testid="admin-bank-details">
      <div>
        <h1 className="font-cinzel text-2xl sm:text-3xl text-white tracking-wide">Bank Details</h1>
        <p className="mt-1 text-muted-foreground text-sm">Bank-transfer details for customers who wish to pay in advance. When enabled, these are appended to reservation confirmation emails with an instruction to use the reservation number as the payment reference.</p>
      </div>

      <GlassCard className="p-6 space-y-4" hover={false}>
        <div className="flex items-center justify-between">
          <h2 className="text-white font-medium flex items-center gap-2"><Landmark className="h-4 w-4 text-gold" /> Account details</h2>
          <label className="flex items-center gap-2 text-sm text-white/80"><Switch data-testid="bank-enabled-toggle" checked={form.enabled} onCheckedChange={(v) => set("enabled", v)} /> Show in emails</label>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label className="text-white/80">Bank Name</Label><Input data-testid="bank-name-input" value={form.bank_name} onChange={(e) => set("bank_name", e.target.value)} placeholder="e.g. Barclays" className={inp} /></div>
          <div><Label className="text-white/80">Account Name</Label><Input data-testid="account-name-input" value={form.account_name} onChange={(e) => set("account_name", e.target.value)} placeholder="Clacton Fireworks Ltd" className={inp} /></div>
          <div><Label className="text-white/80">Sort Code</Label><Input data-testid="sort-code-input" value={form.sort_code} onChange={(e) => set("sort_code", e.target.value)} placeholder="00-00-00" className={inp} /></div>
          <div><Label className="text-white/80">Account Number</Label><Input data-testid="account-number-input" value={form.account_number} onChange={(e) => set("account_number", e.target.value)} placeholder="12345678" className={inp} /></div>
        </div>
        <div className="flex justify-end pt-3 border-t border-white/10">
          <Button className="cf-gold-btn gap-2" onClick={save} disabled={saving} data-testid="bank-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Bank Details
          </Button>
        </div>
      </GlassCard>
    </div>
  );
}
